
import React from "react";
import { cn } from "@/lib/utils";
import { FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

interface MobileFormFieldProps {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

export const MobileFormField = ({ 
  label, 
  error, 
  required, 
  children, 
  className 
}: MobileFormFieldProps) => {
  return (
    <FormItem className={cn("space-y-2", className)}>
      <FormLabel className="text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </FormLabel>
      <FormControl>
        {children}
      </FormControl>
      {error && <FormMessage className="text-xs">{error}</FormMessage>}
    </FormItem>
  );
};

interface MobileInputProps extends React.ComponentProps<typeof Input> {
  label: string;
  error?: string;
  required?: boolean;
}

export const MobileInput = React.forwardRef<HTMLInputElement, MobileInputProps>(
  ({ label, error, required, className, ...props }, ref) => {
    return (
      <MobileFormField label={label} error={error} required={required}>
        <Input
          ref={ref}
          className={cn(
            "h-11 text-base border-input", // Clean, simple styling
            "focus:border-primary focus:ring-1 focus:ring-primary/20",
            "transition-colors duration-200",
            error && "border-destructive focus:border-destructive focus:ring-destructive/20",
            className
          )}
          {...props}
        />
      </MobileFormField>
    );
  }
);

MobileInput.displayName = "MobileInput";

interface MobileTextareaProps extends React.ComponentProps<typeof Textarea> {
  label: string;
  error?: string;
  required?: boolean;
}

export const MobileTextarea = React.forwardRef<HTMLTextAreaElement, MobileTextareaProps>(
  ({ label, error, required, className, ...props }, ref) => {
    return (
      <MobileFormField label={label} error={error} required={required}>
        <Textarea
          ref={ref}
          className={cn(
            "min-h-[100px] text-base border-input",
            "focus:border-primary focus:ring-1 focus:ring-primary/20",
            "transition-colors duration-200",
            "resize-none",
            error && "border-destructive focus:border-destructive focus:ring-destructive/20",
            className
          )}
          {...props}
        />
      </MobileFormField>
    );
  }
);

MobileTextarea.displayName = "MobileTextarea";

interface MobileButtonProps extends React.ComponentProps<typeof Button> {
  loading?: boolean;
  fullWidth?: boolean;
}

export const MobileButton = React.forwardRef<HTMLButtonElement, MobileButtonProps>(
  ({ children, loading, fullWidth, className, disabled, ...props }, ref) => {
    return (
      <Button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          "h-11 text-base font-medium",
          "touch-manipulation",
          "transition-all duration-200",
          fullWidth && "w-full",
          className
        )}
        {...props}
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            Loading...
          </div>
        ) : (
          children
        )}
      </Button>
    );
  }
);

MobileButton.displayName = "MobileButton";
